/**
 * useAnalytics Hook
 *
 * React hook for fetching and managing dashboard analytics data.
 * Provides loading states, error handling, and automatic refetching.
 */

import { useState, useEffect, useCallback } from 'react';
import { getDashboardAnalytics, type DashboardAnalytics } from '../services/analytics.service';

interface UseAnalyticsReturn {
  data: DashboardAnalytics | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * Fetch and manage dashboard analytics data
 *
 * @returns {UseAnalyticsReturn} Analytics data with loading/error states
 *
 * @example
 * const { data, isLoading, error, refetch } = useAnalytics();
 *
 * if (isLoading) return <LoadingSpinner />;
 * if (error) return <ErrorMessage error={error} />;
 * if (data) return <AnalyticsDisplay data={data} />;
 */
export const useAnalytics = (): UseAnalyticsReturn => {
  const [data, setData] = useState<DashboardAnalytics | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchAnalytics = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const analytics = await getDashboardAnalytics();
      setData(analytics);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to fetch analytics');
      setError(error);
      console.error('[useAnalytics] Error fetching analytics:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch analytics on mount
  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchAnalytics,
  };
};

export default useAnalytics;
